import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Swords, Search, Loader2, ArrowRight, TrendingUp, Target, Lightbulb, CheckCircle2,
  XCircle, MinusCircle, ExternalLink, Bookmark, BookmarkCheck, Trash2, Copy, Download, Share2, X,
} from "lucide-react";
import { AIResourcesShell, StepLabel } from "../../components/ai/AIResourcesShell";
import { ResultPaywall } from "../../components/ai/ResultPaywall";
import { aiCompetitors, aiSave, aiListSaved, aiDeleteSaved } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useAIResources } from "../../hooks/useAIResources";
import { copyToClipboard } from "../../lib/clipboard";

const levelMeta = (lvl) => ({
  Low: { pct: 25, color: "#22c55e" }, Medium: { pct: 55, color: "#D4AF37" },
  High: { pct: 80, color: "#f97316" }, "Very High": { pct: 100, color: "#ef4444" },
}[lvl] || { pct: 55, color: "#D4AF37" });

const Cell = ({ v }) => {
  const s = String(v || "").toLowerCase();
  if (s.startsWith("y")) return <CheckCircle2 size={16} className="text-green-500 mx-auto" />;
  if (s.startsWith("p")) return <MinusCircle size={16} className="text-amber-500 mx-auto" />;
  return <XCircle size={16} className="text-gray-300 mx-auto" />;
};

const scoreColor = (s) => (s >= 80 ? "#22c55e" : s >= 60 ? "#D4AF37" : "#ef4444");

export default function CompetitorSearch() {
  const navigate = useNavigate();
  const { user, requireAuth } = useAuth();
  const { competitor, patchCompetitor } = useAIResources();
  const { idea, result } = competitor;
  const [input, setInput] = useState(idea || "");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [active, setActive] = useState(null); // competitor detail modal
  const [savedFlag, setSavedFlag] = useState(false);

  const loadSaved = useCallback(() => {
    if (!user) { setSaved([]); return; }
    aiListSaved("competitor").then(setSaved).catch(() => setSaved([]));
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const run = async () => {
    if (!input.trim()) { toast.error("Describe your startup or idea"); return; }
    setLoading(true); setSavedFlag(false);
    patchCompetitor({ idea: input.trim(), result: null });
    try {
      const r = await aiCompetitors(input.trim());
      patchCompetitor({ result: r });
      setTimeout(() => document.getElementById("comp-results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch { toast.error("Could not analyze competitors. Try again."); }
    finally { setLoading(false); }
  };

  const saveAnalysis = () => {
    if (!result) return;
    if (savedFlag) { toast.info("Already saved — see your Startup Notebook"); return; }
    const doSave = async () => {
      try { await aiSave("competitor", result); setSavedFlag(true); toast.success("Analysis saved to your Startup Notebook"); loadSaved(); }
      catch (e) { console.error("save competitor failed", e); toast.error("Could not save"); }
    };
    if (user) doSave(); else requireAuth(doSave);
  };

  const copySummary = async () => {
    if (!result) return;
    const t = [
      `Competitor Analysis — ${result.idea}`,
      `Competition: ${result.landscape?.competition_level} · Market saturation ${result.landscape?.market_saturation}% · ${result.landscape?.industry_growth}`,
      `Differentiation Score: ${result.differentiation_score}/100`,
      `Top competitors: ${(result.competitors || []).map((c) => c.name).join(", ")}`,
      `Market gaps: ${(result.gaps || []).join("; ")}`,
      `Recommendation: ${result.recommendation}`,
    ].join("\n");
    (await copyToClipboard(t)) ? toast.success("Summary copied") : toast.error("Copy failed");
  };

  const openSaved = (row) => { patchCompetitor({ idea: row.data?.idea || "", result: row.data }); setInput(row.data?.idea || ""); setSavedFlag(true); setTimeout(() => document.getElementById("comp-results")?.scrollIntoView({ behavior: "smooth" }), 150); };
  const delSaved = async (id) => { setSaved((p) => p.filter((x) => x.id !== id)); try { await aiDeleteSaved(id); } catch { loadSaved(); } };

  const sidebar = (
    <div className="rounded-2xl border border-gray-100 bg-white p-5">
      <p className="font-display font-bold text-[#0F0F0F]">Saved Analyses</p>
      <p className="text-xs text-[#687280] mt-1">{saved.length} saved.</p>
      <div className="mt-3 space-y-2.5">
        {saved.length === 0 ? <p className="text-xs text-[#9aa1ab]">Save an analysis to revisit it.</p> : saved.slice(0, 6).map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2" data-testid={`comp-saved-${s.id}`}>
            <button onClick={() => openSaved(s)} className="flex items-center gap-2 min-w-0 text-left">
              <BookmarkCheck size={14} className="text-[#D4AF37] shrink-0" />
              <span className="text-sm text-[#0F0F0F] truncate">{s.data?.idea}</span>
            </button>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-xs font-bold" style={{ color: scoreColor(s.data?.differentiation_score || 0) }}>{s.data?.differentiation_score}</span>
              <button onClick={() => delSaved(s.id)} className="text-[#9aa1ab] hover:text-red-500" data-testid={`comp-saved-del-${s.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const ls = result?.landscape || {};
  const lm = levelMeta(ls.competition_level);

  return (
    <AIResourcesShell active="competitor-search" title="Competitor Search" subtitle="Discover your competitors, analyze their strengths and weaknesses, and identify opportunities to stand out." icon={Swords} extra={sidebar}>
      {/* Search */}
      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="comp-search">
        <StepLabel n={1} />
        <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">Describe your startup</h2>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Describe your startup or business idea... (e.g. AI study platform for students)" className="w-full rounded-full border border-gray-200 pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="comp-input" />
          </div>
          <button onClick={run} disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="comp-search-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Swords size={16} />} Search Competitors
          </button>
        </div>
      </section>

      {loading && <div className="flex items-center justify-center py-16 text-[#687280]" data-testid="comp-loading"><Loader2 className="animate-spin mr-2" /> Analyzing the competitive landscape…</div>}

      {result && (
        <ResultPaywall label="competitor analysis" testid="comp-paywall">
        <div id="comp-results" className="space-y-6 mt-6" data-testid="comp-results">
          {/* Export actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F]">Competitive Analysis</h2>
            <div className="flex items-center gap-2">
              <button onClick={copySummary} className="inline-flex items-center gap-1.5 border border-gray-200 rounded-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:border-[#D4AF37]" data-testid="comp-copy"><Copy size={13} /> Copy Summary</button>
              <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 border border-gray-200 rounded-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:border-[#D4AF37]" data-testid="comp-pdf"><Download size={13} /> Download PDF</button>
              <button onClick={saveAnalysis} className="inline-flex items-center gap-1.5 bg-[#0F0F0F] text-white rounded-full px-4 py-2 text-xs font-bold hover:bg-[#1c1c1c]" data-testid="comp-save-btn"><Bookmark size={13} /> Save Analysis</button>
            </div>
          </div>

          {/* Card 1: Top Competitors */}
          <Card title="Top Competitors" testid="comp-card-competitors">
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#9aa1ab] border-b border-gray-100">
                  <th className="py-2 px-2">Company</th><th className="py-2 px-2">Description</th><th className="py-2 px-2">Strengths</th><th className="py-2 px-2">Weaknesses</th><th className="py-2 px-2">Position</th>
                </tr></thead>
                <tbody>
                  {result.competitors.map((c, i) => (
                    <tr key={i} onClick={() => setActive(c)} className="border-b border-gray-50 hover:bg-[#FBF7EA]/50 cursor-pointer" data-testid={`comp-row-${i}`}>
                      <td className="py-3 px-2 font-semibold text-[#0F0F0F] whitespace-nowrap">{c.name}</td>
                      <td className="py-3 px-2 text-[#687280]">{c.description}</td>
                      <td className="py-3 px-2 text-green-600">{c.strengths}</td>
                      <td className="py-3 px-2 text-red-500">{c.weaknesses}</td>
                      <td className="py-3 px-2 font-semibold text-[#8a6d1a] whitespace-nowrap">{c.market_position}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#9aa1ab] mt-2">Click any competitor for a full breakdown.</p>
          </Card>

          {/* Cards 2 + 5: Landscape + Differentiation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Competitive Landscape" testid="comp-card-landscape">
              <div className="flex items-center justify-between text-sm"><span className="text-[#687280]">Competition Level</span><span className="font-bold" style={{ color: lm.color }}>{ls.competition_level}</span></div>
              <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden mt-2"><div className="h-full rounded-full transition-all" style={{ width: `${lm.pct}%`, background: lm.color }} /></div>
              <div className="grid grid-cols-2 gap-4 mt-5">
                <div className="rounded-xl bg-[#F7F7F7] p-4"><p className="text-[11px] uppercase text-[#9aa1ab] font-bold">Market Saturation</p><p className="font-display text-2xl font-extrabold text-[#0F0F0F]">{ls.market_saturation}%</p></div>
                <div className="rounded-xl bg-[#F7F7F7] p-4"><p className="text-[11px] uppercase text-[#9aa1ab] font-bold flex items-center gap-1"><TrendingUp size={12} /> Industry Growth</p><p className="font-semibold text-[#0F0F0F] mt-1 text-sm">{ls.industry_growth}</p></div>
              </div>
            </Card>

            <Card title="Differentiation Score" testid="comp-card-diff">
              <div className="flex items-center gap-5">
                <Ring score={result.differentiation_score} />
                <p className="text-sm text-[#374151] leading-relaxed flex-1">{result.differentiation_explanation}</p>
              </div>
            </Card>
          </div>

          {/* Card 3 + 4: Insights + Gaps */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="AI Competitive Insights" testid="comp-card-insights">
              <ul className="space-y-2.5">{(result.insights || []).map((x, i) => <li key={i} className="flex gap-2 text-sm text-[#374151]"><Lightbulb size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />{x}</li>)}</ul>
            </Card>
            <Card title="Market Gaps & Opportunities" testid="comp-card-gaps">
              <div className="space-y-2.5">{(result.gaps || []).map((x, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-[#D4AF37]/25 bg-[#FBF7EA]/40 p-3"><span className="w-6 h-6 rounded-full bg-[#D4AF37] text-[#0F0F0F] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span><span className="text-sm font-medium text-[#0F0F0F]">{x}</span></div>
              ))}</div>
            </Card>
          </div>

          {/* Card 6: Advantages */}
          <Card title="Your Competitive Advantages" testid="comp-card-advantages">
            <div className="flex flex-wrap gap-2">{(result.advantages || []).map((a, i) => <span key={i} className="text-xs font-bold bg-[#D4AF37]/15 text-[#8a6d1a] px-3 py-1.5 rounded-full">{a}</span>)}</div>
          </Card>

          {/* Card 7: SWOT */}
          <Card title="SWOT Analysis" testid="comp-card-swot">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Swot title="Strengths" items={result.swot?.strengths} tone="bg-green-50 text-green-700 border-green-100" />
              <Swot title="Weaknesses" items={result.swot?.weaknesses} tone="bg-red-50 text-red-600 border-red-100" />
              <Swot title="Opportunities" items={result.swot?.opportunities} tone="bg-[#FBF7EA] text-[#8a6d1a] border-[#D4AF37]/20" />
              <Swot title="Threats" items={result.swot?.threats} tone="bg-gray-50 text-[#374151] border-gray-100" />
            </div>
          </Card>

          {/* Card 8: Feature comparison */}
          <Card title="Feature Comparison" testid="comp-card-features">
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-[11px] uppercase tracking-wide text-[#9aa1ab] border-b border-gray-100">
                  <th className="py-2 px-2 text-left">Feature</th>
                  <th className="py-2 px-2 text-center text-[#8a6d1a]">Your Startup</th>
                  {(result.feature_comparison?.competitors || []).map((c, i) => <th key={i} className="py-2 px-2 text-center">{c}</th>)}
                </tr></thead>
                <tbody>
                  {(result.feature_comparison?.rows || []).map((r, i) => (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="py-2.5 px-2 font-medium text-[#0F0F0F]">{r.feature}</td>
                      <td className="py-2.5 px-2"><Cell v={r.you} /></td>
                      {(r.values || []).map((v, j) => <td key={j} className="py-2.5 px-2"><Cell v={v} /></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Card 9: Recommendation */}
          <Card title="AI Recommendation" testid="comp-card-reco">
            <p className="text-sm text-[#374151] leading-relaxed">{result.recommendation}</p>
          </Card>

          {/* Card 10: Next steps */}
          <Card title="Recommended Next Steps" testid="comp-card-next">
            <ul className="space-y-2.5">{(result.next_steps || []).map((s, i) => <li key={i} className="flex gap-2 text-sm text-[#374151]"><CheckCircle2 size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />{s}</li>)}</ul>
            <div className="flex flex-wrap gap-3 mt-5">
              <button onClick={() => navigate("/ai-resources/validation-lab")} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#c5a030]" data-testid="comp-continue-validation">Continue to Validation Plan <ArrowRight size={15} /></button>
              <button onClick={saveAnalysis} className="inline-flex items-center gap-2 border border-gray-200 rounded-full px-5 py-2.5 text-sm font-bold text-[#374151] hover:border-[#D4AF37]" data-testid="comp-save-btn-2"><Bookmark size={15} /> Save Analysis</button>
            </div>
          </Card>
        </div>
        </ResultPaywall>
      )}

      {/* Competitor detail modal */}
      {active && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="comp-detail-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div><h2 className="font-display text-2xl font-extrabold text-[#0F0F0F]">{active.name}</h2><p className="text-sm text-[#687280] mt-1">{active.description}</p></div>
              <button onClick={() => setActive(null)} className="text-[#687280] hover:text-[#0F0F0F] shrink-0 ml-2" data-testid="comp-detail-close"><X size={20} /></button>
            </div>
            <div className="mt-5 space-y-3 text-sm">
              <Detail label="Products" v={active.products} />
              <Detail label="Target Audience" v={active.target_audience} />
              <Detail label="Pricing" v={active.pricing} />
              <Detail label="Business Model" v={active.business_model} />
              <Detail label="Market Position" v={active.market_position} />
              <div><p className="text-[11px] font-bold uppercase text-green-600">Strengths</p><p className="text-[#374151]">{active.strengths}</p></div>
              <div><p className="text-[11px] font-bold uppercase text-red-500">Weaknesses</p><p className="text-[#374151]">{active.weaknesses}</p></div>
              <div><p className="text-[11px] font-bold uppercase text-[#D4AF37]">AI Summary</p><p className="text-[#374151] leading-relaxed">{active.ai_summary}</p></div>
              {active.website && <a href={active.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0F0F0F] hover:text-[#8a6d1a]" data-testid="comp-detail-site">Visit website <ExternalLink size={14} /></a>}
            </div>
          </div>
        </div>
      )}
    </AIResourcesShell>
  );
}

const Card = ({ title, testid, children }) => (
  <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={testid}>
    <h3 className="font-display text-lg font-bold text-[#0F0F0F] mb-4">{title}</h3>
    {children}
  </section>
);

const Swot = ({ title, items, tone }) => (
  <div className={`rounded-2xl border p-4 ${tone}`}>
    <p className="font-bold text-sm mb-2">{title}</p>
    <ul className="space-y-1 text-sm">{(items || []).map((x, i) => <li key={i} className="flex gap-1.5">•<span>{x}</span></li>)}</ul>
  </div>
);

const Detail = ({ label, v }) => v ? (
  <div className="flex gap-2"><span className="text-[#9aa1ab] w-32 shrink-0 text-xs font-semibold uppercase pt-0.5">{label}</span><span className="text-[#374151]">{v}</span></div>
) : null;

const Ring = ({ score }) => {
  const r = 46, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative w-28 h-28 shrink-0">
      <svg width="112" height="112" className="-rotate-90">
        <circle cx="56" cy="56" r={r} fill="none" stroke="#f1f1f1" strokeWidth="9" />
        <circle cx="56" cy="56" r={r} fill="none" stroke={scoreColor(score)} strokeWidth="9" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-extrabold text-[#0F0F0F]">{score}</span>
        <span className="text-[10px] text-[#9aa1ab]">/ 100</span>
      </div>
    </div>
  );
};
